import * as React from 'react';
import { withRouter } from 'react-router-dom';

import { inject, observer } from 'mobx-react';

import { getConfig, URLS } from '@constants';

import {
	EAppsCounters,
	EContext,
	ERefsApps,
	EResponseStatus,
	EStore,
} from '@enums';

import { translations, counters } from '@utils';

import { fetchData, commonWillUnmount } from '@decorators';

import {
	IAppsStore,
	IAppState,
} from '@stores';

import { EAppItemType } from '@components';

import { ContentWrapper } from '@ui';

import { AppsPanel, AppsShowcase, AppsGenres } from './Panels';
import { orderedGenres } from './utils/constants';
import { getCompilationVisibility } from './utils/getCompilationVisibility';

import { IPropsCatalogMain } from './types';

import * as s from './CatalogMain.scss';

const isNeedFetch = (store: IAppsStore): boolean => {
	return !store?.data?.apps?.length && store.status !== EResponseStatus.OK;
};

const fetchDataArgs = {
	context: EContext.MOBILE,
	limit: 10,
};

@inject(
	EStore.APPS_INSTALLED,
	EStore.APPS_POPULAR,
	EStore.APPS_NEW,
	EStore.APPS_BY_ID,
	EStore.APPS_SALES,
	EStore.APPS_BY_GENRE_COMPILATION,
	EStore.SHOWCASE,
	EStore.UI,
	EStore.CURRENT_USER,
	EStore.MAIN_MENU_APPS,
	EStore.COUNTERS,
)
@observer
@commonWillUnmount()
@fetchData([
	// showcase
	async ({ showcaseStore, currentUserStore }: IAppState) => {
		if (isNeedFetch(showcaseStore) && getConfig()?.apps.isShowcaseEnable) {
			return await showcaseStore?.fetch({ ...fetchDataArgs, user: currentUserStore.currentUser });
		}
	},

	// Installed apps
	async ({ appsInstalledStore, currentUserStore: { currentUser } }: IAppState) => {
		if (currentUser?.isMyCreated && (isNeedFetch(appsInstalledStore))) {
			return await appsInstalledStore?.fetch({ ...fetchDataArgs, user: currentUser });
		}
	},

	// New apps
	async ({ appsNewStore, currentUserStore }: IAppState) => {
		if (isNeedFetch(appsNewStore) && getConfig()?.apps.isNewEnable) {
			return await appsNewStore?.fetch({ ...fetchDataArgs, user: currentUserStore.currentUser });
		}
	},

	// Popular apps
	async ({ appsPopularStore, currentUserStore }: IAppState) => {
		if (isNeedFetch(appsPopularStore)) {
			return await appsPopularStore?.fetch({ ...fetchDataArgs, user: currentUserStore.currentUser, isShuffle: true });
		}
	},

	// Apps by genre
	async ({ appsByGenreCompilationStore: compilation, currentUserStore }: IAppState) => {
		const appsByGenre = compilation.appsByGenreCompilation;

		return await Promise.all(
			orderedGenres.map( async ({ genre }) => {
				if (!appsByGenre || (appsByGenre && !appsByGenre[genre]?.data?.length && appsByGenre[genre]?.status !== EResponseStatus.OK)) {
					return await compilation?.fetch(genre, { ...fetchDataArgs, user: currentUserStore.currentUser });
				}
			}),
		);
	},

	// Sales
	async ({ appsSalesStore, currentUserStore }: IAppState) => {
		if (isNeedFetch(appsSalesStore) && getConfig()?.apps.isSalesEnable) {
			return await appsSalesStore?.fetch({ ...fetchDataArgs, user: currentUserStore.currentUser, target: 'catalog' });
		}
	},

	// UI STATE
	({ uiStore }: IAppState) => {
		if (uiStore.status !== EResponseStatus.OK) {
			uiStore.setTopMenuInitialState({ isShowIcons: true });
		}
	},
])
class CatalogMainView extends React.PureComponent<IPropsCatalogMain> {

	componentWillUnmount() {
		const {
			appsNewStore,
			appsPopularStore,
			appsInstalledStore,
		} = this.props;

		appsNewStore.clearStore();
		appsPopularStore.clearStore();
		appsInstalledStore.clearStore();
	}

	onClickInstalledAppsPageLink = () => {
		counters.sendRB(EAppsCounters.SHOW_MORE_INSTALLED);
	};

	onClickNewAppsPageLink = () => {
		counters.sendRB(EAppsCounters.SHOW_MORE_NEW);
	};

	onClickPopularAppsPageLink = () => {
		counters.sendRB(EAppsCounters.SHOW_MORE_POPULAR);
	};

	render() {
		const {
			appsInstalledStore,
			appsNewStore,
			appsPopularStore,
			appsSalesStore,
			currentUserStore,
			showcaseStore,
			appsByGenreCompilationStore,
		} = this.props;
		const isShowInstalled = currentUserStore.isMyCreated &&
			!(appsInstalledStore && appsInstalledStore.status === EResponseStatus.OK && !appsInstalledStore.data.apps.length);
		const isShowSales = (appsSalesStore?.data?.apps?.length && appsSalesStore.status === EResponseStatus.OK) ||
			appsSalesStore.status === EResponseStatus.PENDING ||
			appsSalesStore.status === EResponseStatus.ERROR;

		return (
			<ContentWrapper
				className={s.block}
			>
				<AppsShowcase
					store={showcaseStore}
					isVisible={Boolean(getCompilationVisibility(showcaseStore)) && getConfig()?.apps.isShowcaseEnable}
				/>

				<AppsPanel
					title={translations.appCatalog('appsInstalled')}
					linkUrl={URLS.CATALOG_INSTALLED}
					type={EAppItemType.SMALL}
					refUrl={ERefsApps.LINE_INSTALLED}
					store={appsInstalledStore}
					isVisible={isShowInstalled}
					onLinkClick={this.onClickInstalledAppsPageLink}
				/>

				<AppsPanel
					title={translations.appCatalog('appsNew')}
					linkUrl={URLS.CATALOG_NEW}
					refUrl={ERefsApps.LINE_INSTALLED}
					store={appsNewStore}
					isVisible={Boolean(getCompilationVisibility(appsNewStore)) && getConfig()?.apps.isNewEnable}
					onLinkClick={this.onClickNewAppsPageLink}
				/>

				<AppsPanel
					title={translations.appCatalog('appsPopular')}
					linkUrl={URLS.CATALOG_POPULAR}
					refUrl={ERefsApps.LINE_INSTALLED}
					store={appsPopularStore}
					isVisible={Boolean(getCompilationVisibility(appsPopularStore))}
					onLinkClick={this.onClickPopularAppsPageLink}
				/>

				<AppsPanel
					title={translations.appCatalog('appSales')}
					linkUrl={URLS.CATALOG_POPULAR}
					refUrl={ERefsApps.LINE_SALES}
					type={EAppItemType.WIDE}
					store={appsSalesStore}
					isVisible={isShowSales && getConfig()?.apps.isSalesEnable}
					onLinkClick={this.onClickPopularAppsPageLink}
					hasDescription
				/>

				<AppsGenres store={appsByGenreCompilationStore}/>
			</ContentWrapper>
		);
	}

}

export const CatalogMain = withRouter(CatalogMainView);
